import { Person } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportRoleScope,
  AnalyticsReportTypes,
  useEmployeesCostCenterData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';

import { useEmployeesCostCenterReport } from './useEmployeesCostCenterReport';

export const EmployeesCostCenterReport = () => {
  const { t } = useTranslation();

  const apiRef = useGridApiRef();

  const defaultReportSettings: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
    },
  };

  const reportRequestProps = useReportDataset({
    selection: defaultReportSettings,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.AltManager,
        ],
      },
    },
  });

  const { data, isLoading, failedToLoad } = useEmployeesCostCenterData(
    reportRequestProps.reportSelection,
  );

  const { columns, rows } = useEmployeesCostCenterReport({ data });

  return (
    <ReportViewLayout
      heading={t('laEmpCostCentersReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDatasetSelector
                {...reportRequestProps}
                isLoading={isLoading}
                failedToLoad={failedToLoad}
              />
              <ReportDatasetRowCount
                count={rows.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility apiRef={apiRef} isLoading={isLoading} />
              <ReportDataExport
                title={t('laEmpCostCentersReport')}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={isLoading}
        initialState={{ density: 'compact' }}
        getRowId={(row) => row.employeeId}
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            padding: 1,
            paddingBottom: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'center',
          },
        }}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
      />
    </ReportViewLayout>
  );
};
